import { z } from 'zod';
import { SkuDivision, SkuType } from './master.js';
import { BoundingBox, Confidence, Id, Timestamp } from './primitives.js';
/**
 * POSM — Point-of-Sale Material (materi promosi di outlet).
 *
 * A second thing a salesperson records during a visit, ALONGSIDE the shelf
 * photographs the vision pipeline reads for planogram and price. A POSM is a
 * promotional item — a standing display, a chiller brand wrap, a banner — and
 * the only question asked of it is binary: is it PRESENT at this outlet or not.
 *
 * It is deliberately NOT a SKU. There is no rack, no planogram, no price, no
 * facing count. The salesperson photographs ONE item, focused, and the cloud
 * model compares that photo against the item's REFERENCE IMAGE and answers
 * present / absent. So the master here is shaped after `sku_groupings` — a
 * `code` natural key, display text, `active` — plus the one thing that makes the
 * detection possible: a reference image the model matches against.
 *
 * Two kinds of data, the same split the questionnaire makes:
 *   - The ITEMS are master data the console maintains (GLOBAL, `posm:write`).
 *   - The OBSERVATIONS are visit data recorded in the field, scoped by region
 *     like every other visit fact, read back through `report:read`.
 *
 * The observation photo is uploaded straight to object storage under its own
 * `posm/<visit>/…` prefix — NOT a `visit_photos` row. A POSM photo is not a
 * planogram: it must never go through the shelf vision pipeline, count against
 * the per-visit photo cap, or be swept by the visit-photo retention. Its own
 * lightweight detection fills in the result on the observation row.
 */
/**
 * Where a POSM observation is in its detection lifecycle.
 *
 *   PENDING       — the photo is uploaded, the model has not answered yet.
 *   DETECTED      — the reference item was found in the photo. "Ada".
 *   NOT_DETECTED  — the model looked and did not find it. "Tidak ada".
 *   NEEDS_REVIEW  — the model was not confident enough to call it either way;
 *                   a human should look. Kept distinct from NOT_DETECTED so an
 *                   uncertain reading is never reported as a firm absence.
 *   FAILED        — the analysis itself errored (model down, timeout). A process
 *                   failure, NOT a verdict about the item — the photo can be
 *                   re-analysed.
 */
export const PosmDetectionStatus = z.enum([
    'PENDING',
    'DETECTED',
    'NOT_DETECTED',
    'NEEDS_REVIEW',
    'FAILED',
]);
/* -------------------------------------------------------------------------- */
/* The POSM item master                                                       */
/* -------------------------------------------------------------------------- */
export const PosmItem = z.object({
    id: Id,
    /** The natural key — stable across a rename, like `channels` / `sku_groupings`. */
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    /**
     * An optional note — doubles as a hint for the detection prompt ("banner merah
     * logo BELL'S") and as context for a reader of the report. Null where none.
     */
    description: z.string().nullable(),
    /** Internal (ours) or a competitor's — the same axis as a SKU (`SkuType`). */
    type: SkuType,
    /** Set only on a KOMPETITOR item, like a SKU. Null otherwise. */
    competitorId: Id.nullable(),
    /**
     * The SKU category/grouping masters, REUSED — a POSM is classified on the same
     * two axes as a SKU, and the console reads them from the same masters. NULLABLE
     * (unlike a SKU's, which are NOT NULL) only because items created before this
     * existed have none yet; the console form requires one on save. `…Name` is the
     * joined label, null when unset.
     */
    categoryId: Id.nullable(),
    categoryName: z.string().nullable(),
    groupingId: Id.nullable(),
    groupingName: z.string().nullable(),
    /** Null on KOMPETITOR, set on INTERNAL — the same pairing as a SKU. */
    division: SkuDivision.nullable(),
    /**
     * How many reference images this item has (0–4) — the SKU list's "n/4". The
     * images live on their own sub-resource (`PosmReferenceImage`), fetched per item
     * when the console form opens; the list only needs the count. An item with 0
     * cannot be detected, so the console flags it.
     */
    referenceImageCount: z.number().int().nonnegative(),
    active: z.boolean(),
});
/**
 * Create a POSM item — code, name, optional note. Reference images are attached
 * afterwards on the item's own sub-resource (they need its id), exactly like a
 * SKU's four reference photos.
 */
export const CreatePosmItemRequest = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    description: z.string().max(500).trim().nullable().default(null),
    type: SkuType,
    competitorId: z.string().uuid().nullable().default(null),
    categoryId: z.string().uuid(),
    groupingId: z.string().uuid(),
    division: SkuDivision.nullable().default(null),
});
/**
 * Partial update. `code` is absent for the reason it is absent from every other
 * master update here: it is the natural key, and renaming it would strand the
 * observations already recorded against the item. Reference images are managed on
 * their own sub-resource, not here.
 */
export const UpdatePosmItemRequest = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(500).trim().nullable(),
    type: SkuType,
    competitorId: z.string().uuid().nullable(),
    categoryId: z.string().uuid(),
    groupingId: z.string().uuid(),
    division: SkuDivision.nullable(),
})
    .partial();
/* -------------------------------------------------------------------------- */
/* Reference images — up to four per item, the model's comparators            */
/* -------------------------------------------------------------------------- */
/**
 * The four slots a POSM's reference images occupy — the same vocabulary as a
 * SKU's reference sides. For a POSM the labels are nominal (a banner has no real
 * "back"); they exist so re-uploading a slot REPLACES that slot rather than
 * piling up, exactly as for a SKU.
 */
export const PosmSide = z.enum(['FRONT', 'BACK', 'LEFT', 'RIGHT']);
/**
 * One reference image read back — what the console's four-slot grid renders.
 * No embedding here (unlike a SKU image): POSM matching is an in-prompt compare
 * in the cloud model, so there is nothing to index and no "searchable yet" bit.
 */
export const PosmReferenceImage = z.object({
    id: Id,
    posmItemId: Id,
    side: PosmSide,
    objectKey: z.string().min(1),
    contentType: z.string().min(1),
    /** Short-lived presigned GET; absent (null) on write responses. */
    viewUrl: z.string().url().nullable(),
});
/** Step 3 of the upload: the object landed — record it against its slot. */
export const RegisterPosmImageRequest = z.object({
    side: PosmSide,
    objectKey: z.string().min(1),
    contentType: z.string().min(1),
});
/* -------------------------------------------------------------------------- */
/* Image uploads — presign                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Presign an upload for a POSM item's reference image — the console's version.
 * Same three-step shape as a SKU image and the questionnaire example image: the
 * image goes straight to object storage, never through the api, and
 * `contentLength` is bound into the signature so the URL cannot be reused to
 * upload something else.
 */
export const PresignPosmReferenceImageRequest = z.object({
    contentType: z.enum(['image/jpeg', 'image/webp', 'image/png']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
export const PresignPosmReferenceImageResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/**
 * Presign an upload for a field observation photo — the salesperson's shot of
 * the POSM. Same shape; a different storage prefix (`posm/<visit>/…`) the server
 * validates before storing the key on an observation.
 */
export const PresignPosmPhotoRequest = z.object({
    contentType: z.enum(['image/jpeg', 'image/webp', 'image/png']),
    contentLength: z.number().int().positive().max(10 * 1024 * 1024),
});
export const PresignPosmPhotoResponse = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/* -------------------------------------------------------------------------- */
/* Field submission — one observation at a time                                */
/* -------------------------------------------------------------------------- */
/**
 * Submit ONE POSM photo for detection in a visit.
 *
 * The salesperson does NOT pick an item — they just upload or take a photo of
 * whatever promotional material they see, exactly like the SKU capture. The model
 * then matches the photo against the whole POSM catalogue (each item's reference
 * image) and decides WHICH item, if any, it shows. So the request carries only
 * the photo; the matched item comes back in the result.
 */
export const SubmitPosmObservationRequest = z.object({
    photoObjectKey: z.string().min(1).max(500),
});
/* -------------------------------------------------------------------------- */
/* Read models                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * One POSM observation read back — the item's display fields denormalised beside
 * the detection result, so a reader renders a row without a second lookup. Shared
 * by the field app (what have I already shot this visit) and the console report.
 */
export const PosmObservationView = z.object({
    /**
     * The observation row's own id — the stable React key and what a delete targets
     * (a no-match reading has no item id to key on).
     */
    id: Id,
    /**
     * The matched catalogue item, or NULL when the photo matched no known POSM — a
     * "tidak ada POSM" reading, STORED and shown exactly like a SKU photo with zero
     * detections rather than dropped. `code` and `name` are null in that case.
     */
    posmItemId: Id.nullable(),
    code: z.string().nullable(),
    name: z.string().nullable(),
    status: PosmDetectionStatus,
    /** The model's confidence in its verdict, 0–100. Null until analysed. */
    confidence: Confidence.nullable(),
    /** A short reason the model gave, when it gave one. Null otherwise. */
    aiNote: z.string().nullable(),
    /** Short-lived presigned GET for the observation photo. */
    photoUrl: z.string().url().nullable(),
    /**
     * Where the model found the POSM in the field photo — normalised 0–1, top-left
     * origin, the same convention as a shelf detection. Null when the model gave no
     * box (it is asked for one but not required). The field app draws it over the
     * photo like the SKU reading's boxes.
     */
    bbox: BoundingBox.nullable(),
    /** When the verdict was reached. Null while PENDING. */
    analyzedAt: Timestamp.nullable(),
});
/**
 * The outcome of submitting one POSM photo.
 *
 * `matched` is true when the model identified a catalogue POSM. Either way the
 * photo is RECORDED and `observation` is the stored row: a match is a DETECTED /
 * NEEDS_REVIEW reading of that item, and a no-match is a NOT_DETECTED reading with
 * no item (`posmItemId` null) — shown as a "tidak ada POSM" card exactly like a SKU
 * photo with zero detections. `observation` is null only when nothing could be
 * analysed at all (an empty catalogue, or an ai-svc failure to retry); `message`
 * says which.
 */
export const PosmSubmitResult = z.object({
    matched: z.boolean(),
    observation: PosmObservationView.nullable(),
    /** A short sentence for the salesperson — the verdict, or why nothing matched. */
    message: z.string(),
});
export const VisitPosmView = z.object({
    visitId: Id,
    observations: z.array(PosmObservationView),
});
/**
 * One row of the "Laporan POSM" report — a VISIT in which a salesperson recorded
 * at least one POSM observation.
 *
 * PER VISIT, one row per visit, NOT one per observation: a single visit where the
 * salesperson photographed two or three POSM is ONE report entry, and the
 * individual readings (which item, present or not, the photo, the box, the AI
 * reason) live in the detail the row opens. Its own console surface, standalone
 * from the visit report, for the reason the questionnaire list is: a POSM recorded
 * on a visit that never pressed "Kirim data" has no visit-report row to hang off.
 */
export const PosmReportRow = z.object({
    visitId: Id,
    checkInAt: Timestamp,
    salesUserName: z.string(),
    outletName: z.string(),
    outletCode: z.string(),
    regionName: z.string(),
    /** How many POSM were photographed in this visit. */
    observationCount: z.number().int().nonnegative(),
    /** How many of those came back DETECTED ("Ada"). */
    detectedCount: z.number().int().nonnegative(),
});
//# sourceMappingURL=posm.js.map